import { ErrorHandler, Injectable } from '@angular/core';

/**
 * Global {@link ErrorHandler} that recovers from stale lazy-chunk failures after a deploy.
 *
 * When a new build is deployed, a browser tab still running the previous build may try to
 * lazy-load a content-hashed chunk (router `loadComponent`/`loadChildren`, `@defer` blocks, or
 * manual dynamic `import()`) whose hash no longer matches the freshly deployed assets. esbuild's
 * runtime throws one of the messages matched below. In that case we force a single full reload so
 * the browser fetches the current `index.html` and the correct chunk hashes.
 *
 * A `sessionStorage` guard prevents a reload loop; it is cleared on the next successful page
 * `load` so future stale-deploy events can still self-heal.
 *
 * Ported from the shared `@ta/ng-core` `ChunkLoadErrorHandler` used by Frontend-WebCat.
 */
@Injectable()
export class ChunkLoadErrorHandler extends ErrorHandler {
  private static readonly STORAGE_KEY = 'chunk-load-error';

  constructor() {
    super();
    // Clear the reload guard on successful page load so future errors can still trigger a reload.
    window.addEventListener('load', () =>
      sessionStorage.removeItem(ChunkLoadErrorHandler.STORAGE_KEY),
    );
  }

  override handleError(error: unknown): void {
    const message =
      error != null && typeof error === 'object' && 'message' in error
        ? String((error as { message: unknown }).message)
        : '';

    const isChunkError =
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Failed to load module script') ||
      message.includes('Importing a module script failed') ||
      message.includes('error loading dynamically imported module');

    if (isChunkError && !sessionStorage.getItem(ChunkLoadErrorHandler.STORAGE_KEY)) {
      sessionStorage.setItem(ChunkLoadErrorHandler.STORAGE_KEY, '1');
      window.location.reload();
      return;
    }

    super.handleError(error);
  }
}
